import type { FeatureEvents, ScreenEvent, UIEvent } from '../new-analytics-types';
import { Action, ActionSubject } from '../new-analytics-types';

type ScreenKeys = 'rovoDevTabScreen';

type UIEventKeys = string;

type TrackEventKeys = string;

type OperationalEventKeys = string;

export const rovoDevTabScreen = 'rovoDevTabScreen';

export enum SettingToggleActionSubjectId {
  rovoDevEnabledSetting = 'rovoDevEnabledSetting',
  rovoDevCliEnabledSetting = 'rovoDevCliEnabledSetting',
  rovoDevSessionsEnabledSetting = 'rovoDevSessionsEnabledSetting',
  rovoDevReviewEnabledSetting = 'rovoDevReviewEnabledSetting',
  rovoDevIdeVscodeEnabledSetting = 'rovoDevIdeVscodeEnabledSetting',
  rovoDevAutomationsEnabledSetting = 'rovoDevAutomationsEnabledSetting',
  rovoDevBitbucketPipelinesEnabledSetting = 'rovoDevBitbucketPipelinesEnabledSetting',
}

const rovoDevAttributes = {
  targetProduct: 'rovodev',
  productStage: 'onboarding',
  onboardingStep: 'enablement',
  containerLevel: 'org',
};

export const rovoDevTabEvents: FeatureEvents<ScreenKeys, UIEventKeys, TrackEventKeys, OperationalEventKeys> = {
  confluenceSpec:
    'https://hello.atlassian.net/wiki/spaces/~71202090f8c67fd6754ecfb7831cbb4141da5f/pages/5745460982/WIP+Analytics+Plan+for+RovoDev+GA+Launch',
  screen: {
    rovoDevTabScreen: (): ScreenEvent => ({
      data: {
        name: rovoDevTabScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/88031',
    }),
  },
  ui: {
    toggleRovoDevEnabledSetting: (attributes: { enabled: boolean; isAdmin: boolean; userId: string; orgPolicyId?: string }): UIEvent => ({
      data: {
        action: Action.Toggled,
        actionSubject: ActionSubject.Toggle,
        actionSubjectId: SettingToggleActionSubjectId.rovoDevEnabledSetting,
        source: rovoDevTabScreen,
        attributes: {
          ...attributes,
          ...rovoDevAttributes,
          xid: 'rovodev',
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/88078',
    }),
    toggleRovoDevCliEnabledSetting: (attributes: {
      enabled: boolean;
      isAdmin: boolean;
      userId: string;
      orgPolicyId?: string;
    }): UIEvent => ({
      data: {
        action: Action.Toggled,
        actionSubject: ActionSubject.Toggle,
        actionSubjectId: SettingToggleActionSubjectId.rovoDevCliEnabledSetting,
        source: rovoDevTabScreen,
        attributes: {
          ...attributes,
          ...rovoDevAttributes,
          xid: 'rovodev-cli',
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/88897',
    }),
    toggleRovoDevSessionsEnabledSetting: (attributes: {
      enabled: boolean;
      isAdmin: boolean;
      userId: string;
      orgPolicyId?: string;
    }): UIEvent => ({
      data: {
        action: Action.Toggled,
        actionSubject: ActionSubject.Toggle,
        actionSubjectId: SettingToggleActionSubjectId.rovoDevSessionsEnabledSetting,
        source: rovoDevTabScreen,
        attributes: {
          ...attributes,
          ...rovoDevAttributes,
          xid: 'rovodev-sessions',
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91722',
    }),
    toggleRovoDevReviewEnabledSetting: (attributes: {
      enabled: boolean;
      isAdmin: boolean;
      userId: string;
      orgPolicyId?: string;
    }): UIEvent => ({
      data: {
        action: Action.Toggled,
        actionSubject: ActionSubject.Toggle,
        actionSubjectId: SettingToggleActionSubjectId.rovoDevReviewEnabledSetting,
        source: rovoDevTabScreen,
        attributes: {
          ...attributes,
          ...rovoDevAttributes,
          xid: 'rovodev-review',
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/88925',
    }),
    toggleRovoDevIdeVscodeEnabledSetting: (attributes: {
      enabled: boolean;
      isAdmin: boolean;
      userId: string;
      orgPolicyId?: string;
    }): UIEvent => ({
      data: {
        action: Action.Toggled,
        actionSubject: ActionSubject.Toggle,
        actionSubjectId: SettingToggleActionSubjectId.rovoDevIdeVscodeEnabledSetting,
        source: rovoDevTabScreen,
        attributes: {
          ...attributes,
          ...rovoDevAttributes,
          xid: 'rovodev-ide-vscode',
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/89463',
    }),
    toggleRovoDevAutomationsEnabledSetting: (attributes: {
      enabled: boolean;
      isAdmin: boolean;
      userId: string;
      orgPolicyId?: string;
    }): UIEvent => ({
      data: {
        action: Action.Toggled,
        actionSubject: ActionSubject.Toggle,
        actionSubjectId: SettingToggleActionSubjectId.rovoDevAutomationsEnabledSetting,
        source: rovoDevTabScreen,
        attributes: {
          ...attributes,
          ...rovoDevAttributes,
          xid: 'rovodev-automations',
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/90356',
    }),
    toggleRovoDevBitbucketPipelinesEnabledSetting: (attributes: {
      enabled: boolean;
      isAdmin: boolean;
      userId: string;
      orgPolicyId?: string;
    }): UIEvent => ({
      data: {
        action: Action.Toggled,
        actionSubject: ActionSubject.Toggle,
        actionSubjectId: SettingToggleActionSubjectId.rovoDevBitbucketPipelinesEnabledSetting,
        source: rovoDevTabScreen,
        attributes: {
          ...attributes,
          ...rovoDevAttributes,
          xid: 'rovodev-in-pipelines',
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/92649',
    }),
  },
  track: {},
  operational: {},
};
